import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, Send, Undo2, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UniqueBadge } from "@/components/Logo";
import { btnGhost, btnPrimary, ProductStatus } from "./ui";
import { PhotoManager } from "./editor/PhotoManager";
import { AiDraftPanel } from "./editor/AiDraftPanel";
import { ProductForm, toForm, toPayload } from "./editor/ProductForm";
import { InventoryPanel } from "./editor/InventoryPanel";

export default function AdminProductEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [cats, setCats] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/products/${id}`).then((r) => { setP(r.data); setForm(toForm(r.data)); setDirty(false); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const save = async (silent = false) => {
    if (!dirty) return p;
    setBusy(true);
    try {
      const base = toPayload(toForm(p));
      const next = toPayload(form);
      const diff = Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(base[k])));
      if (diff.clear_sale_price && p.sale_price == null) delete diff.clear_sale_price;
      const { data } = await api.patch(`/admin/products/${id}`, diff);
      setP(data); setForm(toForm(data)); setDirty(false);
      if (!silent) toast.success("Modifiche salvate");
      return data;
    } catch (e) { toast.error(apiError(e)); throw e; } finally { setBusy(false); }
  };
  const action = async (path, msg) => {
    try {
      await save(true);
      const { data } = await api.post(`/admin/products/${id}/${path}`);
      setP(data); setForm(toForm(data)); toast.success(msg);
    } catch (e) { if (e.response) toast.error(apiError(e)); }
  };
  const archive = async () => {
    if (!window.confirm("Archiviare il prodotto? Non sarà più visibile.")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Prodotto archiviato"); navigate("/admin/prodotti"); } catch (e) { toast.error(apiError(e)); }
  };
  const onPhotos = (d) => setP((cur) => ({ ...cur, photos: d.photos, images: d.images, image_alts: d.image_alts, videos: d.videos }));
  const onAiUpdate = (d) => {
    const before = toForm(p);
    const fresh = toForm(d);
    setForm((cur) => Object.fromEntries(Object.keys(fresh).map((k) => [k, JSON.stringify(cur[k]) === JSON.stringify(before[k]) ? fresh[k] : cur[k]])));
    setP(d);
  };

  if (!p || !form) return <div className="h-64 bg-[#E9DFCE]/50 animate-pulse" />;

  return (
    <div data-testid="admin-product-editor" className="pb-24">
      <Link to="/admin/prodotti" data-testid="editor-back-link" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#956842] mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Prodotti</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2"><ProductStatus status={p.status} testId="editor-status" />{p.stock === 1 && <UniqueBadge />}<span className="text-xs text-[#242321]/55" data-testid="editor-sku">{p.sku}</span></div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#39251D]" data-testid="editor-title">{p.name}</h1>
          {p.status === "draft" && !isOwner && <p className="text-xs text-[#956842] mt-1">In attesa di approvazione del titolare. Le modifiche ai prodotti pubblicati li riportano in bozza.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {p.status === "published" && <a href={`/prodotto/${p.slug}`} target="_blank" rel="noreferrer" data-testid="editor-view-shop" className={btnGhost}><Eye className="w-4 h-4" /> Vedi</a>}
          {p.status === "published" && <button data-testid="editor-unpublish" onClick={() => action("unpublish", "Prodotto ritirato dalla vendita")} className={btnGhost}><Undo2 className="w-4 h-4" /> Ritira</button>}
          {isOwner && <button data-testid="editor-archive" onClick={archive} className={btnGhost}><Archive className="w-4 h-4" /></button>}
        </div>
      </div>
      <div className="grid xl:grid-cols-[1.1fr_1fr] gap-5 items-start">
        <div className="space-y-5">
          <PhotoManager product={p} onChange={onPhotos} />
          <AiDraftPanel product={p} autoStart={sp.get("analizza") === "1"} onAiUpdate={onAiUpdate} />
          <InventoryPanel product={p} onChange={(stock) => setP((cur) => ({ ...cur, stock }))} />
        </div>
        <ProductForm form={form} update={update} cats={cats} />
      </div>
      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-30 bg-[#F5F0E8]/95 backdrop-blur border-t border-[#D5B98C]/60 px-4 sm:px-8 py-3 flex items-center justify-end gap-2">
        {dirty && <span className="mr-auto text-xs text-[#956842]" data-testid="editor-unsaved">Modifiche non salvate</span>}
        <button data-testid="editor-save" disabled={busy || !dirty} onClick={() => save()} className={btnGhost}><Save className="w-4 h-4" /> Salva bozza</button>
        {isOwner && p.status !== "published" && <button data-testid="editor-publish" disabled={busy} onClick={() => action("publish", "Approvato e pubblicato")} className={btnPrimary}><Send className="w-4 h-4" /> Approva e pubblica</button>}
      </div>
    </div>
  );
}
